import { useEffect, useState } from "react";
import api from "../services/api";
import "../styles/pos.css";

const Inventory = () => {
  // 🔐 Logged-in user (example structure)
  // { role: "ADMIN" } OR { role: "MANAGER", branch_id: 2 }
  const user = JSON.parse(localStorage.getItem("user"));

  const [branches, setBranches] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);

  const [form, setForm] = useState({
    branch_id: "",
    product_id: "",
    quantity: ""
  });

  // ==============================
  // LOAD DATA (ROLE AWARE)
  // ==============================
  const loadAll = async () => {
    try {
      // --- Branch API ---
      const branchRequest =
        user.role === "ADMIN"
          ? api.get("/branches")
          : api.get(`/branches/`);

      // --- Inventory API ---
      const inventoryRequest =
        user.role === "ADMIN"
          ? api.get("/inventory")
          : api.get(`/inventory?branch_id=${user.branch_id}`);

      const [b, p, s] = await Promise.all([
        branchRequest,
        api.get("/products"),
        inventoryRequest
      ]);

      // Normalize branch response (single vs list)
      setBranches(
        Array.isArray(b.data) ? b.data : [b.data]
      );

      setProducts(p.data || []);
      setStock(s.data || []);
    } catch (err) {
      alert("Failed to load inventory data");
    }
  };

  // ==============================
  // INIT
  // ==============================
  useEffect(() => {
    loadAll();

    // Auto-select branch for MANAGER
    if (user.role === "MANAGER") {
      setForm(f => ({
        ...f,
        branch_id: user.branch_id
      }));
    }
  }, []);

  // ==============================
  // ADD / UPDATE STOCK
  // ==============================
  const addStock = async () => {
    if (!form.product_id || !form.quantity) {
      alert("All fields required");
      return;
    }

    try {
      await api.post("/inventory", {
        branch_id:
          user.role === "MANAGER"
            ? user.branch_id
            : Number(form.branch_id),
        product_id: Number(form.product_id),
        quantity: Number(form.quantity)
      });

      setForm({
        branch_id:
          user.role === "MANAGER" ? user.branch_id : "",
        product_id: "",
        quantity: ""
      });

      loadAll();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to add stock");
    }
  };

  // ==============================
  // UI
  // ==============================
  return (
    <div className="page-container">
      {/* ===== HEADER ===== */}
      <div className="page-header">
        <h2>Inventory</h2>
      </div>

      {/* ===== ADD STOCK ===== */}
      <div className="card">
        <h4>Add / Update Stock</h4>

        <div className="form-row">
          {/* BRANCH */}
          <select
            value={form.branch_id}
            disabled={user.role === "MANAGER"}
            onChange={e =>
              setForm({
                ...form,
                branch_id: e.target.value
              })
            }
          >
            <option value="">Select Branch</option>
            {branches.map(b => (
              <option
                key={b.branch_id}
                value={b.branch_id}
              >
                {b.branch_name}
              </option>
            ))}
          </select>

          {/* PRODUCT */}
          <select
            value={form.product_id}
            onChange={e =>
              setForm({
                ...form,
                product_id: e.target.value
              })
            }
          >
            <option value="">Select Product</option>
            {products.map(p => (
              <option
                key={p.product_id}
                value={p.product_id}
              >
                {p.name}
              </option>
            ))}
          </select>

          {/* QTY */}
          <input
            type="number"
            placeholder="Quantity"
            value={form.quantity}
            onChange={e =>
              setForm({
                ...form,
                quantity: e.target.value
              })
            }
          />

          <button
            className="primary-btn"
            onClick={addStock}
          >
            Save
          </button>
        </div>
      </div>

      {/* ===== STOCK LIST ===== */}
      <div className="invoice-table print-container">
        <table>
          <thead>
            <tr>
              <th>Branch</th>
              <th>Product</th>
              <th>Seller</th>
              <th>Barcode</th>
              <th>Quantity</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {stock.length === 0 && (
              <tr>
                <td colSpan="5" align="center">
                  No stock data
                </td>
              </tr>
            )}

            {stock.map(s => (
              <tr key={s.inventory_id}>
                <td>{s.branch?.branch_name}</td>
                <td>{s.product?.name}</td>
                <td>{s.product?.seller_name || "-"}</td>
                <td>{s.product?.barcode || "-"}</td>
                <td>
                  {s.editing ? (
                    <input
                      type="number"
                      value={s.newQuantity}
                      onChange={e => {
                        setStock(prev =>
                          prev.map(i =>
                            i.inventory_id ===
                            s.inventory_id
                              ? {
                                  ...i,
                                  newQuantity:
                                    e.target.value
                                }
                              : i
                          )
                        );
                      }}
                      style={{ width: 70 }}
                    />
                  ) : (
                    <b>{s.quantity}</b>
                  )}
                </td>
                <td>
                  {s.editing ? (
                    <button
                      className="second-action-btn"
                      onClick={async () => {
                        await api.put(
                          `/inventory/${s.inventory_id}`,
                          {
                            quantity: Number(
                              s.newQuantity
                            )
                          }
                        );
                        loadAll();
                      }}
                    >
                      Save
                    </button>
                  ) : (
                    <button
                      className="second-action-btn"
                      onClick={() => {
                        setStock(prev =>
                          prev.map(i =>
                            i.inventory_id ===
                            s.inventory_id
                              ? {
                                  ...i,
                                  editing: true,
                                  newQuantity:
                                    i.quantity
                                }
                              : i
                          )
                        );
                      }}
                    >
                      Edit
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Inventory;
